import React, { useState, useEffect } from 'react';
import { toast } from 'react-toastify';
import { Image as ImageIcon, Upload, X, Save } from 'lucide-react';
import axios from 'axios';

const API_URL = process.env.REACT_APP_API_URL || '/api';

const ServiceImagesEditor = () => {
  const [settings, setSettings] = useState({
    services_image_enabled: 'false',
    services_image_position: 'right', // 'left', 'right', 'both'
    services_image_left: '',
    services_image_right: '',
    addons_image_enabled: 'false',
    addons_image_position: 'right',
    addons_image_left: '',
    addons_image_right: '',
  });
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [uploadingLeft, setUploadingLeft] = useState({ services: false, addons: false });
  const [uploadingRight, setUploadingRight] = useState({ services: false, addons: false });

  useEffect(() => {
    loadSettings();
  }, []);

  const loadSettings = async () => {
    try {
      const token = sessionStorage.getItem('token');
      const config = { headers: { Authorization: `Bearer ${token}` } };

      const keys = [
        'services_image_enabled',
        'services_image_position',
        'services_image_left',
        'services_image_right',
        'addons_image_enabled',
        'addons_image_position',
        'addons_image_left',
        'addons_image_right',
      ];

      const responses = await Promise.all(
        keys.map(key =>
          axios.get(`${API_URL}/business-info/${key}`, config)
            .catch(() => ({ data: { info_value: '' } }))
        )
      );

      const newSettings = {};
      keys.forEach((key, index) => {
        newSettings[key] = responses[index].data.info_value || (key.includes('enabled') ? 'false' : key.includes('position') ? 'right' : '');
      });

      setSettings(newSettings);
      setLoading(false);
    } catch (error) {
      console.error('Failed to load service image settings:', error);
      toast.error('Failed to load settings');
      setLoading(false);
    }
  };

  const handleImageUpload = async (file, type, position) => {
    if (!file) return;

    const setUploading = position === 'left' ? setUploadingLeft : setUploadingRight;
    setUploading(prev => ({ ...prev, [type]: true }));

    try {
      const formData = new FormData();
      formData.append('image', file);

      const token = sessionStorage.getItem('token');
      const response = await axios.post(`${API_URL}/upload/image`, formData, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'multipart/form-data',
        },
      });

      const imageUrl = response.data.url;
      const settingKey = `${type}_image_${position}`;
      
      setSettings(prev => ({
        ...prev,
        [settingKey]: imageUrl,
      }));

      toast.success('Image uploaded successfully');
    } catch (error) {
      console.error('Image upload error:', error);
      toast.error('Failed to upload image: ' + (error.response?.data?.error || error.message));
    } finally {
      setUploading(prev => ({ ...prev, [type]: false }));
    }
  };

  const handleRemoveImage = (type, position) => {
    const settingKey = `${type}_image_${position}`;
    setSettings(prev => ({
      ...prev,
      [settingKey]: '',
    }));
  };

  const handleSaveSettings = async () => {
    setSaving(true);
    try {
      const token = sessionStorage.getItem('token');
      const config = {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
      };

      await Promise.all(
        Object.entries(settings).map(([key, value]) =>
          axios.put(`${API_URL}/business-info/${key}`, { value }, config)
        )
      );

      toast.success('Service image settings saved successfully');
      window.dispatchEvent(new Event('serviceImagesUpdated'));
    } catch (error) {
      console.error('Failed to save settings:', error);
      toast.error('Failed to save settings: ' + (error.response?.data?.error || error.message));
    } finally {
      setSaving(false);
    }
  };

  const renderImageUpload = (type, position, label) => {
    const settingKey = `${type}_image_${position}`;
    const imageUrl = settings[settingKey];
    const isUploading = position === 'left' ? uploadingLeft[type] : uploadingRight[type];

    return (
      <div>
        <label className="block text-sm font-medium text-gray-700 mb-2">{label}</label>
        {imageUrl ? (
          <div className="relative">
            <img
              src={imageUrl}
              alt={`${type} ${position}`}
              className="w-full h-48 object-cover rounded-lg border border-gray-300"
            />
            <button
              onClick={() => handleRemoveImage(type, position)}
              className="absolute top-2 right-2 p-2 bg-red-500 text-white rounded-full hover:bg-red-600 transition-colors"
              title="Remove image"
            >
              <X size={16} />
            </button>
          </div>
        ) : (
          <div className="border-2 border-dashed border-gray-300 rounded-lg p-6 text-center">
            <input
              type="file"
              accept="image/*"
              onChange={(e) => handleImageUpload(e.target.files[0], type, position)}
              className="hidden"
              id={`upload-${type}-${position}`}
              disabled={isUploading}
            />
            <label
              htmlFor={`upload-${type}-${position}`}
              className="cursor-pointer flex flex-col items-center"
            >
              {isUploading ? (
                <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary-600"></div>
              ) : (
                <>
                  <Upload className="text-gray-400 mb-2" size={32} />
                  <span className="text-sm text-gray-600">Click to upload image</span>
                  <span className="text-xs text-gray-500 mt-1">PNG, JPG up to 5MB</span>
                </>
              )}
            </label>
          </div>
        )}
      </div>
    );
  };

  if (loading) {
    return (
      <div className="card">
        <div className="flex items-center justify-center py-8">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary-600"></div>
        </div>
      </div>
    );
  }

  return (
    <div className="card">
      <div className="flex items-center space-x-3 mb-6">
        <ImageIcon className="text-primary-600" size={24} />
        <h2 className="text-2xl font-bold">Service & Add-on Images</h2>
      </div>

      <p className="text-gray-600 mb-6">
        Add decorative images to your service and add-on selection pages in the client booking flow. Images can appear on the left, right, or both sides.
      </p>

      {/* Services Images */}
      <div className="mb-8">
        <h3 className="text-xl font-semibold mb-4">Services Section Images</h3>
        
        {/* Enable Services Images */}
        <div className="bg-gray-50 p-4 rounded-lg mb-4">
          <label className="flex items-center space-x-2 cursor-pointer">
            <input
              type="checkbox"
              checked={settings.services_image_enabled === 'true'}
              onChange={(e) => setSettings(prev => ({ 
                ...prev, 
                services_image_enabled: e.target.checked ? 'true' : 'false' 
              }))}
              className="w-4 h-4 text-primary-600 rounded focus:ring-primary-500"
            />
            <span className="text-sm font-medium">Enable Images for Services Section</span>
          </label>
        </div>

        {settings.services_image_enabled === 'true' && (
          <>
            {/* Position Selection */}
            <div className="mb-4">
              <label className="block text-sm font-medium text-gray-700 mb-2">Image Position</label>
              <div className="flex space-x-4">
                <label className="flex items-center space-x-2 cursor-pointer">
                  <input
                    type="radio"
                    value="left"
                    checked={settings.services_image_position === 'left'}
                    onChange={(e) => setSettings(prev => ({ ...prev, services_image_position: e.target.value }))}
                    className="text-primary-600 focus:ring-primary-500"
                  />
                  <span className="text-sm">Left Only</span>
                </label>
                <label className="flex items-center space-x-2 cursor-pointer">
                  <input
                    type="radio"
                    value="right"
                    checked={settings.services_image_position === 'right'}
                    onChange={(e) => setSettings(prev => ({ ...prev, services_image_position: e.target.value }))}
                    className="text-primary-600 focus:ring-primary-500"
                  />
                  <span className="text-sm">Right Only</span>
                </label>
                <label className="flex items-center space-x-2 cursor-pointer">
                  <input
                    type="radio"
                    value="both"
                    checked={settings.services_image_position === 'both'}
                    onChange={(e) => setSettings(prev => ({ ...prev, services_image_position: e.target.value }))}
                    className="text-primary-600 focus:ring-primary-500"
                  />
                  <span className="text-sm">Both Sides</span>
                </label>
              </div>
            </div>

            {/* Image Uploads */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              {(settings.services_image_position === 'left' || settings.services_image_position === 'both') && 
                renderImageUpload('services', 'left', 'Left Image')}
              {(settings.services_image_position === 'right' || settings.services_image_position === 'both') && 
                renderImageUpload('services', 'right', 'Right Image')}
            </div>
          </>
        )}
      </div>

      {/* Add-ons Images */}
      <div className="mb-8">
        <h3 className="text-xl font-semibold mb-4">Add-ons Section Images</h3>
        
        {/* Enable Add-ons Images */}
        <div className="bg-gray-50 p-4 rounded-lg mb-4">
          <label className="flex items-center space-x-2 cursor-pointer">
            <input
              type="checkbox"
              checked={settings.addons_image_enabled === 'true'}
              onChange={(e) => setSettings(prev => ({ 
                ...prev, 
                addons_image_enabled: e.target.checked ? 'true' : 'false' 
              }))}
              className="w-4 h-4 text-primary-600 rounded focus:ring-primary-500"
            />
            <span className="text-sm font-medium">Enable Images for Add-ons Section</span>
          </label>
        </div>

        {settings.addons_image_enabled === 'true' && (
          <>
            {/* Position Selection */}
            <div className="mb-4">
              <label className="block text-sm font-medium text-gray-700 mb-2">Image Position</label>
              <div className="flex space-x-4">
                <label className="flex items-center space-x-2 cursor-pointer">
                  <input
                    type="radio"
                    value="left"
                    checked={settings.addons_image_position === 'left'}
                    onChange={(e) => setSettings(prev => ({ ...prev, addons_image_position: e.target.value }))}
                    className="text-primary-600 focus:ring-primary-500"
                  />
                  <span className="text-sm">Left Only</span>
                </label>
                <label className="flex items-center space-x-2 cursor-pointer">
                  <input
                    type="radio"
                    value="right"
                    checked={settings.addons_image_position === 'right'}
                    onChange={(e) => setSettings(prev => ({ ...prev, addons_image_position: e.target.value }))}
                    className="text-primary-600 focus:ring-primary-500"
                  />
                  <span className="text-sm">Right Only</span>
                </label>
                <label className="flex items-center space-x-2 cursor-pointer">
                  <input
                    type="radio"
                    value="both"
                    checked={settings.addons_image_position === 'both'}
                    onChange={(e) => setSettings(prev => ({ ...prev, addons_image_position: e.target.value }))}
                    className="text-primary-600 focus:ring-primary-500"
                  />
                  <span className="text-sm">Both Sides</span>
                </label>
              </div>
            </div>

            {/* Image Uploads */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              {(settings.addons_image_position === 'left' || settings.addons_image_position === 'both') && 
                renderImageUpload('addons', 'left', 'Left Image')}
              {(settings.addons_image_position === 'right' || settings.addons_image_position === 'both') && 
                renderImageUpload('addons', 'right', 'Right Image')}
            </div>
          </>
        )}
      </div>

      {/* Save Button */}
      <button
        onClick={handleSaveSettings}
        disabled={saving}
        className="btn btn-primary flex items-center space-x-2"
      >
        <Save size={18} />
        <span>{saving ? 'Saving...' : 'Save Image Settings'}</span>
      </button>
    </div>
  );
};

export default ServiceImagesEditor;
